import { useEffect, useState } from "react";
import { api } from "../../api/client";

export interface AssistRequest {
    action: string;
    text: string;
    tone?: string;
    prompt?: string;
    context?: { subject?: string; bodyText?: string };
}

interface AssistResult {
    usageId: string;
    proposals: string[];
}

/**
 * AI proposals never overwrite content silently: the user sees the original
 * next to each proposal and explicitly accepts, regenerates or cancels.
 */
export function AiProposalDialog({
    request,
    onAccept,
    onClose,
}: {
    request: AssistRequest;
    onAccept: (text: string) => void;
    onClose: () => void;
}) {
    const [result, setResult] = useState<AssistResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const runRequest = () => {
        setLoading(true);
        setError(null);
        api.post<AssistResult>("/api/v1/ai/assist", request)
            .then(setResult)
            .catch((e) => setError(e instanceof Error ? e.message : String(e)))
            .finally(() => setLoading(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(runRequest, []);

    const feedback = (accepted: boolean) => {
        if (result) void api.post("/api/v1/ai/feedback", { usageId: result.usageId, accepted });
    };

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                feedback(false);
                onClose();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result]);

    return (
        <div className="edm-modal-backdrop" role="dialog" aria-modal="true" aria-label="AI suggestion" data-testid="ai-dialog">
            <div className="edm-modal ai-modal">
                <h3>AI suggestion — {request.action.replace(/-/g, " ")}</h3>
                {request.text && (
                    <div className="ai-original">
                        <div className="insp-label">Original</div>
                        <div className="ai-text">{request.text}</div>
                    </div>
                )}
                {loading && <p className="status-line">Thinking…</p>}
                {error && <p className="status-line error">{error}</p>}
                {result && !loading && (
                    <div className="ai-proposals">
                        <div className="insp-label">
                            {result.proposals.length > 1 ? "Suggestions (click to use)" : "Suggestion"}
                        </div>
                        {result.proposals.map((p, i) => (
                            <button
                                key={i}
                                type="button"
                                className="ai-proposal"
                                data-testid={`ai-proposal-${i}`}
                                onClick={() => {
                                    feedback(true);
                                    onAccept(p);
                                    onClose();
                                }}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                )}
                <div className="modal-actions">
                    <button
                        type="button"
                        className="tb-btn"
                        onClick={() => {
                            feedback(false);
                            onClose();
                        }}
                    >
                        Cancel
                    </button>
                    <button type="button" className="tb-btn" onClick={runRequest} disabled={loading}>
                        Regenerate
                    </button>
                </div>
            </div>
        </div>
    );
}
